import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import fallbackRates from '../assets/data/currencies/eurofxref.json';
import { fetchDailyRates } from '../lib/ecbFx';
import './FinanceTicker.css';

const RATES_CACHE_KEY = 'finance-ticker-cache';
const DAILY_REFRESH_MS = 24 * 60 * 60 * 1000;

function fallbackFromBundledSnapshot() {
  return Object.entries(fallbackRates.cube)
    .filter(([code]) => code !== 'EUR')
    .map(([currency, rate]) => ({ currency, rate }));
}

export function shouldRefreshRates(lastUpdatedAt, now = Date.now()) {
  return now - lastUpdatedAt >= DAILY_REFRESH_MS;
}

export function getNextDailyRefreshDelay(now = new Date()) {
  const nextRefresh = new Date(now);
  nextRefresh.setHours(24, 0, 0, 0);
  return Math.max(nextRefresh.getTime() - now.getTime(), 1000);
}

function readCachedRates() {
  if (typeof window === 'undefined') return null;

  try {
    const cached = window.localStorage.getItem(RATES_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    return parsed && parsed.rates ? parsed : null;
  } catch (error) {
    console.warn('Unable to read cached finance rates:', error);
    return null;
  }
}

function writeCachedRates(rates, updatedAt) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates, updatedAt }));
  } catch (error) {
    console.warn('Unable to cache finance rates:', error);
  }
}

export default function FinanceTicker() {
  const [rates, setRates] = useState(fallbackFromBundledSnapshot());
  const [isLive, setIsLive] = useState(false);
  const [offset, setOffset] = useState(0);

  const refreshRates = useCallback(async (forceRefresh = false) => {
    const cached = readCachedRates();
    const now = Date.now();

    if (!forceRefresh && cached && !shouldRefreshRates(cached.updatedAt, now)) {
      setRates(cached.rates);
      setIsLive(true);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 6000);

    try {
      const parsed = await fetchDailyRates(controller.signal);

      if (parsed.length) {
        setRates(parsed);
        setIsLive(true);
        writeCachedRates(parsed, now);
        return;
      }

      throw new Error('No rates were returned from the ECB feed.');
    } catch (error) {
      if (cached) {
        setRates(cached.rates);
        setIsLive(true);
      } else {
        setRates(fallbackFromBundledSnapshot());
        setIsLive(false);
      }

      console.warn('Falling back to the latest available currency snapshot:', error.message);
    } finally {
      window.clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    refreshRates();
  }, [refreshRates]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      refreshRates(true);
    }, getNextDailyRefreshDelay());

    return () => window.clearTimeout(timer);
  }, [refreshRates]);

  const track = useMemo(() => [...rates, ...rates], [rates]);

  const handleScroll = useCallback((direction) => {
    const step = 240;
    setOffset((current) => {
      const maxOffset = Math.max(0, track.length * 120 - 900);
      const nextOffset = direction === 'left' ? current - step : current + step;
      return Math.max(-maxOffset, Math.min(0, nextOffset));
    });
  }, [track.length]);

  return (
    <div className='finance-ticker' role='region' aria-label='Live currency exchange rates'>
      <div className='finance-ticker__controls'>
        <button
          type='button'
          className='finance-ticker__control'
          onClick={() => handleScroll('left')}
          aria-label='Scroll ticker left'
        >
          ←
        </button>
        <button
          type='button'
          className='finance-ticker__control'
          onClick={() => handleScroll('right')}
          aria-label='Scroll ticker right'
        >
          →
        </button>
      </div>
      <div className='finance-ticker__viewport'>
        <div className='finance-ticker__track' style={{ transform: `translateX(${offset}px)` }}>
          {track.map((r, idx) => (
            <Link
              className='finance-ticker__item'
              to={`/finances?currency=${r.currency}`}
              key={`${r.currency}-${idx}`}
              title={`View EUR/${r.currency} rate history`}
            >
              <strong>EUR/{r.currency}</strong> {r.rate.toFixed(4)}
            </Link>
          ))}
        </div>
      </div>
      <p className='finance-ticker__source'>
        {isLive
          ? `Live reference rates from the European Central Bank (${rates.length} currencies) — click any pair for its history`
          : 'Reference rates (offline snapshot) — source: European Central Bank'}
      </p>
    </div>
  );
}
